import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Select, { components, InputActionMeta, MenuPlacement } from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MovablePlaceholder from '../MovablePlaceholder/MovablePlaceholder';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import { useTranslation } from 'react-i18next';
import withErrorBoundary from 'components/ui/withErrorBoundaries';
import ReactCountryFlag from 'react-country-flag';

interface SelectProps {
  options: any[];
  value: any;
  onChange: (value: any) => void;
  onInputChange?: (value: string, actionMeta?: InputActionMeta) => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  onlyLtr?: boolean;
  appendToRight?: boolean;
  isMulti?: boolean;
  movablePlaceholder?: string;
  movablePlaceholderIcon?: any;
  movablePlaceholderFlag?: string;
  movablePlaceholderAlwaysSmall?: boolean;
  movablePlaceholderSm?: boolean;
  highInput?: boolean;
  isLoading?: boolean;
  filterOption?: ((option: FilterOptionOption<any>, inputValue: string) => boolean) | undefined;
  menuIsOpen?: boolean;
  needToTranslate?: boolean;
  menuPosition?: 'fixed' | 'absolute';
  tabIndex?: number;
  isDropdown?: boolean;
  appendToBody?: boolean;
  menuPortralClassName?: string;
  menuPlacement?: MenuPlacement;
  showOnlyIcon?: boolean;
  showHtmlPrev?: boolean;
  showHtmlText?: boolean;
  onBlur?: () => void;
  saveInputValue?: boolean;
}

export interface SelectCustomRef {
  blur: () => void;
  focus: () => void;
}

const SelectCustom = forwardRef<SelectCustomRef, SelectProps>((props, ref) => {
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const [value, setValue] = useState(props.value);
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [options, setOptions] = useState<any[]>(props.options);
  const { t } = useTranslation();
  const containerRef = useRef<any>(null);
  const selectRef = useRef<any>(null);
  const [hightlighedInput, setHightlighedInput] = useState(false);

  const findGroupedOptionByValue = (options: any[], value: any) => {
    const group = options.find((item) => {
      return item.options?.find((option: any) => option.value === value);
    });
    return group?.options?.find((option: any) => option.value === value);
  };

  const getGroupedOptionsForMulti = (options: any[], values: any[]) => {
    if (!values?.length) return [];

    const groups = options.filter((item) => {
      return item.options?.find((option: any) => values.includes(option.value));
    });

    return groups.reduce((acc: any[], group) => {
      return [...acc, ...group.options.filter((option: any) => values.includes(option.value))];
    }, []);
  };

  const getFlatOptionsForMulti = (options: any[], values: any[]) => {
    return options.filter((item) => values.includes(item.value));
  };

  const findOptionByValue = (options: any[], value: any) => {
    return options.find((item) => item.value === value) || '';
  };

  const updateValueFromProps = () => {
    const { options, value, isMulti } = props;

    if (!options?.length) {
      setValue('');
      return;
    }

    const hasGroupedOptions = options[0]?.options;

    if (hasGroupedOptions && isMulti) {
      const result = getGroupedOptionsForMulti(options, value);
      setValue(result);
    } else if (hasGroupedOptions) {
      const result = findGroupedOptionByValue(options, value);
      setValue(result);
    } else if (isMulti && typeof value === 'object') {
      const result = getFlatOptionsForMulti(options, value);
      setValue(result);
    } else {
      const result = findOptionByValue(options, value);
      setValue(result);
    }
  };

  const updateOptionsAndValue = () => {
    const { options, value } = props;

    if (!options?.length) {
      setOptions([]);
      setValue('');
      return;
    }

    setOptions(options);

    const hasGroupedOptions = options[0]?.options;

    if (hasGroupedOptions) {
      const result = findGroupedOptionByValue(options, value);
      setValue(result);
    } else {
      const newValue = findOptionByValue(options, value);
      setValue(newValue);
    }
  };

  useEffect(() => {
    updateValueFromProps();
  }, [props.value, props.options?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    updateOptionsAndValue();
  }, [props.options]); // eslint-disable-line

  useImperativeHandle(ref, () => ({
    blur: () => selectRef.current?.blur(),
    focus: () => selectRef.current?.focus(),
  }));

  const onInputChange = (value: string, meta?: InputActionMeta) => {
    if (!props.isDropdown) {
      setInputValue(value);
      props.onInputChange && props.onInputChange(value, meta);
    }
  };
  const SingleValue = ({ children, ...propss }: any) => {
    const { data } = propss;

    if (data?.html && props.showHtmlPrev) {
      return <components.SingleValue {...propss}>{data.html}</components.SingleValue>;
    }

    if (props.showHtmlPrev) {
      return <components.SingleValue {...propss}>{data.label}</components.SingleValue>;
    }

    const translatedContent =
      props.needToTranslate && typeof children === 'string' ? t(children) : children;

    return (
      <components.SingleValue {...propss}>
        <span>{translatedContent}</span>
        {data?.icon && props.showOnlyIcon && (
          <span>
            <FontAwesomeIcon icon={data.icon.path} />
          </span>
        )}
      </components.SingleValue>
    );
  };

  const containsHebrew = (): boolean => {
    const hebrewPattern = /[\u0590-\u05FF]/;
    const label = options?.[0]?.options?.[0]?.label || options?.[0]?.label;
    const data = props.needToTranslate ? t(label) : label;
    return hebrewPattern.test(data);
  };
  const Option = (option: any) => (
    <components.Option {...option}>
      <div className={containsHebrew() ? 'select-custom__rtl_option' : 'select-custom__ltr_option'}>
        {!!option.data.icon && (
          <span>
            <FontAwesomeIcon icon={option.data.icon.path} className={option.data.icon.className} />
          </span>
        )}
        {!!option.data.flag && (
          <span>
            <ReactCountryFlag svg countryCode={option.data.flag} className='mr-5' />
          </span>
        )}
        {!!option.data.html && option.data.html}
        {!option.data.html && (
          <span>{props.needToTranslate ? t(option.data.label) : option.data.label}</span>
        )}
      </div>
    </components.Option>
  );

  const GroupHeading = (propss: any) => {
    return (
      <components.GroupHeading {...propss}>
        {props.needToTranslate ? t(propss.children) : propss.children}
      </components.GroupHeading>
    );
  };

  return (
    <div
      className={
        'position-relative' +
        (hightlighedInput ? ' select-custom__highlighted' : '') +
        (props.movablePlaceholderSm ? ' movable-placeholder-input-sm' : '')
      }
      ref={containerRef}
    >
      <Select
        ref={selectRef}
        menuPortalTarget={props.appendToBody ? document.body : null}
        styles={{
          menu: (provided) => ({
            ...provided,
            zIndex: props.appendToBody ? 5 : provided.zIndex,
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: props.appendToBody ? 5 : base.zIndex,
            minWidth: props.appendToBody ? 80 : base.minWidth,
          }),
        }}
        classNames={{
          menuPortal: () => props.menuPortralClassName || '',
        }}
        menuPlacement={props.menuPlacement}
        options={options}
        value={value}
        onInputChange={onInputChange}
        filterOption={props.filterOption}
        isLoading={props.isLoading}
        menuIsOpen={props.menuIsOpen}
        inputValue={inputValue}
        onKeyDown={() => {
          setHightlighedInput(false);
        }}
        onChange={(data) => {
          if (props.isMulti) {
            props.onChange(data.map((item: any) => item.value));
          } else {
            props.onChange(data.value);
          }
          if (props.saveInputValue) {
            selectRef.current?.blur();
          }
        }}
        classNamePrefix="select-custom"
        className={
          (props.invalid ? 'select-custom__invalid' : '') +
          (props.onlyLtr ? ' select-custom__only-ltr' : '') +
          (props.appendToRight ? ' select-custom__append-to-right' : '') +
          (props.highInput ? ' select-custom__high' : '') +
          (props.isMulti ? ' select-custom__multi' : '') +
          (props.movablePlaceholder ? ' movable-placeholder-input' : '') +
          (!props.movablePlaceholderIcon &&
            !props.movablePlaceholderFlag &&
            props.movablePlaceholder
            ? ' movable-placeholder-input-noicon'
            : '')
        }
        placeholder={props.placeholder || ''}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: colorContrast,
          },
        })}
        isDisabled={props.disabled}
        onFocus={() => {
          setHightlighedInput(true);
          setIsFocused(true);
          if (props.saveInputValue) {
            setInputValue(value?.label);
          }
        }}
        onBlur={() => {
          setIsFocused(false);
          props.onBlur?.();
        }}
        onMenuClose={() => setHightlighedInput(false)}
        isMulti={props.isMulti}
        menuPosition={props.menuPosition}
        components={{ Option, SingleValue, GroupHeading }}
        tabIndex={props.tabIndex}
      />
      {props.movablePlaceholder && (
        <MovablePlaceholder
          label={props.movablePlaceholder}
          showSmall={
            !!(
              inputValue ||
              (value &&
                (props.isMulti ? Array.isArray(value) && value.length > 0 : value !== '')) ||
              isFocused ||
              props.movablePlaceholderAlwaysSmall
            )
          }
          icon={props.movablePlaceholderIcon}
          flag={props.movablePlaceholderFlag}
        />
      )}
    </div>
  );
});

export default withErrorBoundary(SelectCustom, 'SelectCustom');
